import React, { useState } from "react";
import { Ban, Check, Loader, RotateCcw, TriangleAlert } from "lucide-react";

import { TransactionsGrid } from "./TransactionsGrid";
import { YesNoModal } from "../../../components/src/YesNoModal";
import { BankAccountTransactionInput, TransactionStatus, UpdateTransactionInput } from "../types.generated";
import { ICommandBarMenuItem } from "../../../components/src/common/CommandBar";

interface IUpdateTransactionsStatusGrid {
    items: any;
    visibleColumns?: string[];
    showExcelButton?: boolean;
    onUpdateStatus(input: UpdateTransactionInput[] | BankAccountTransactionInput[]): any;
    highlightRowOnClick?: boolean;
}

export const UpdateTransactionsStatusGrid = ({
    items,
    visibleColumns,
    showExcelButton = true,
    onUpdateStatus,
    highlightRowOnClick = false
}: IUpdateTransactionsStatusGrid): React.ReactElement => {
    const [selected, setSelected] = useState(null);
    const [modal, setModal] = useState({ showModal: false, payload: { input: [] } });
    const [spinner, setSpinner] = useState(false);
    const hasSelected = selected && selected.length > 0;

    const selectedChange = (e: any[]) => {
        setSelected(e);
    };

    const commands: ICommandBarMenuItem[] = [
        {
            itemKey: "newItem",
            text: "Change selection status",
            subMenuProps: {
                items: [
                    {
                        itemKey: "pending",
                        text: "Change to Pending",
                        icon: <RotateCcw />,
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((t: any) => {
                                return { _id: t._id, status: TransactionStatus.Pending };
                            });
                            onUpdateStatus(input);
                        }
                    },
                    {
                        itemKey: "preliminary",
                        text: "Change to Preliminary",
                        icon: <TriangleAlert />,
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((t: any) => {
                                return { _id: t._id, status: TransactionStatus.Preliminary };
                            });
                            onUpdateStatus(input);
                        }
                    },
                    {
                        itemKey: "confirmed",
                        text: "Change to Confirmed",
                        icon: <Check />,
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((t: any) => {
                                return { _id: t._id, status: TransactionStatus.Confirmed };
                            });
                            onUpdateStatus(input);
                        }
                    },
                    {
                        itemKey: "instructed",
                        text: "Change to Instructed",
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((t: any) => {
                                return { _id: t._id, status: TransactionStatus.Instructed };
                            });
                            onUpdateStatus(input);
                        }
                    },
                    {
                        itemKey: "settled",
                        text: "Change to Settled",
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((t: any) => {
                                return { _id: t._id, status: TransactionStatus.Settled };
                            });
                            onUpdateStatus(input);
                        }
                    },
                    {
                        itemKey: "internal",
                        text: "Change to Internal",
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((t: any) => {
                                return { _id: t._id, status: TransactionStatus.Internal };
                            });
                            onUpdateStatus(input);
                        }
                    },
                    {
                        itemKey: "history",
                        text: "Change to History",
                        onClick: () => {
                            setSpinner(true);
                            const input = selected.map((t: any) => {
                                return { _id: t._id, status: TransactionStatus.History };
                            });
                            onUpdateStatus(input);
                        }
                    },
                    {
                        itemKey: "deleted",
                        text: "Change to Deleted",
                        icon: <Ban />,
                        onClick: () => {
                            const input = selected.map((t: any) => {
                                return { _id: t._id, status: TransactionStatus.Deleted };
                            });
                            setModal({
                                showModal: true,
                                payload: { input: input }
                            });
                        }
                    }
                ]
            }
        }
    ];

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete selected transactions?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: { input: modal.payload.input }
                    }}
                    setModal={setModal}
                    onYes={() => {
                        setSpinner(true);
                        onUpdateStatus(modal.payload.input as unknown as UpdateTransactionInput[]);
                    }}
                    onNo={() => {
                        setSpinner(false);
                    }}
                />
            ) : null}
            {spinner ? (
                <span>
                    <Loader /> Updating status...
                </span>
            ) : null}
            <TransactionsGrid
                items={items}
                onSelectedChange={selectedChange}
                commands={hasSelected ? commands : []}
                visibleColumns={visibleColumns}
                showExcelButton={showExcelButton}
                highlightRowOnClick={highlightRowOnClick}
            />
        </div>
    );
};
